import type { ViewConfig } from "@nexus/types"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

export interface ContactsViewState {
  /** Active view ID (synced from URL) */
  activeViewId: string | null
  /** Local config overrides not yet saved to backend */
  localOverrides: Partial<ViewConfig> | null
  /** Whether there are unsaved changes */
  isDirty: boolean
  /** Which group values are expanded (collapsed groups tracked by their value) */
  collapsedGroups: string[]
  /** Currently focused cell for keyboard navigation */
  focusedCell: { rowIndex: number; colIndex: number } | null
  /** Which toolbar panel is open */
  openPanel: "filter" | "sort" | "group" | "columns" | "density" | "color" | null
  /** Column resize in progress */
  columnResizing: { fieldKey: string; startWidth: number; startX: number } | null
}

const initialState: ContactsViewState = {
  activeViewId: null,
  localOverrides: null,
  isDirty: false,
  collapsedGroups: [],
  focusedCell: null,
  openPanel: null,
  columnResizing: null
}

const contactsViewSlice = createSlice({
  name: "contactsView",
  initialState,
  reducers: {
    setActiveViewId(state, action: PayloadAction<string | null>) {
      state.activeViewId = action.payload
      // Clear overrides when switching views
      state.localOverrides = null
      state.isDirty = false
    },

    setLocalOverrides(state, action: PayloadAction<Partial<ViewConfig>>) {
      state.localOverrides = {
        ...state.localOverrides,
        ...action.payload
      }
      state.isDirty = true
    },

    clearOverrides(state) {
      state.localOverrides = null
      state.isDirty = false
    },

    toggleGroupCollapse(state, action: PayloadAction<string>) {
      const value = action.payload
      const idx = state.collapsedGroups.indexOf(value)
      if (idx >= 0) {
        state.collapsedGroups.splice(idx, 1)
      } else {
        state.collapsedGroups.push(value)
      }
    },

    setFocusedCell(state, action: PayloadAction<{ rowIndex: number; colIndex: number } | null>) {
      state.focusedCell = action.payload
    },

    setOpenPanel(state, action: PayloadAction<ContactsViewState["openPanel"]>) {
      // Toggle off if same panel clicked
      state.openPanel = state.openPanel === action.payload ? null : action.payload
    },

    closePanel(state) {
      state.openPanel = null
    },

    startColumnResize(
      state,
      action: PayloadAction<{ fieldKey: string; startWidth: number; startX: number }>
    ) {
      state.columnResizing = action.payload
    },

    endColumnResize(state) {
      state.columnResizing = null
    }
  }
})

export const {
  setActiveViewId,
  setLocalOverrides,
  clearOverrides,
  toggleGroupCollapse,
  setFocusedCell,
  setOpenPanel,
  closePanel,
  startColumnResize,
  endColumnResize
} = contactsViewSlice.actions

export const contactsViewReducer = contactsViewSlice.reducer
